import type { SensorData } from '../../apis/settings';

export type SensorFormRow = {
  sensor?: string;
  sensor_tracking_id?: string;
  product_ids?: string[];
};

export type SensorSelectRow = Partial<SensorData> & { sensor_label?: string };

/** Minimal shipment shape for building initial `sensor_rows` in edit flows. */
export type ShipmentLikeForSensorRows = {
  sensors?: Array<{
    sensor_id?: string;
    sensor_tracking_id?: string;
  }>;
  sensor?: { sensor_id?: string; id?: string; type?: string };
  sensor_tracking_id?: string | null;
  teams?: Array<{ id: string }>;
};

export function initialSensorRowsFromShipment(
  shipment: ShipmentLikeForSensorRows,
  sensorsList: SensorSelectRow[]
): SensorFormRow[] {
  const apiSensors = shipment.sensors;
  if (Array.isArray(apiSensors) && apiSensors.length > 0) {
    const shipmentTeamIds = new Set(
      (shipment.teams ?? []).map((t) => String(t.id))
    );
    return apiSensors.map((ss) => {
      const sid = ss.sensor_id != null ? String(ss.sensor_id) : '';
      const matches = sensorsList.filter((s) => String(s.sensor_id) === sid);
      if (matches.length === 0) {
        return {
          sensor: undefined,
          sensor_tracking_id: String(ss.sensor_tracking_id ?? ''),
        };
      }
      const pick =
        matches.find((m) =>
          (m.teams ?? []).some((t) => shipmentTeamIds.has(String(t.id)))
        ) ?? matches[0];
      // V2 grouped rows may omit `id` when no primary credential is flagged;
      // CustomDynamicSelect falls back to sensor_id — keep form value in sync.
      return {
        sensor: pick.id ?? pick.sensor_id,
        sensor_tracking_id: String(ss.sensor_tracking_id ?? ''),
      };
    });
  }

  const rawSensor = shipment.sensor as { sensor_id?: string } | undefined;
  const sensorFk = rawSensor?.sensor_id;
  if (!sensorFk || sensorsList.length === 0) {
    return [];
  }
  const sid = String(sensorFk);
  const matches = sensorsList.filter((s) => String(s.sensor_id) === sid);
  if (matches.length === 0) {
    return [];
  }
  const shipmentTeamIds = new Set(
    (shipment.teams ?? []).map((t) => String(t.id))
  );
  const pick =
    matches.find((m) =>
      (m.teams ?? []).some((t) => shipmentTeamIds.has(String(t.id)))
    ) ?? matches[0];
  const tracking =
    (shipment.sensor_tracking_id != null &&
      String(shipment.sensor_tracking_id)) ||
    String(shipment.sensor?.id ?? '');
  return [
    {
      sensor: pick.id ?? pick.sensor_id,
      sensor_tracking_id: tracking,
    },
  ];
}

export type SensorPayloadItem = {
  sensor_id: string;
  sensor_tracking_id: string;
  priority: number;
  product_ids?: string[];
};

export type BuiltSensorsPayload =
  { ok: true; sensors: SensorPayloadItem[] } | { ok: false; message: string };

export function buildSensorsPayloadFromRows(
  rows: SensorFormRow[] | undefined,
  sensorsList: SensorSelectRow[],
  options: { includeProductIds?: boolean } = {}
): BuiltSensorsPayload {
  const sensors: SensorPayloadItem[] = [];
  if (!rows?.length) {
    return { ok: true, sensors };
  }
  const seenTrackingIds = new Set<string>();
  for (const r of rows) {
    const companyId = r?.sensor;
    const rawTid = (r?.sensor_tracking_id ?? '').toString();
    const tid = rawTid.trim();
    if (rawTid.length > 0 && tid === '') {
      return {
        ok: false,
        message: 'Only spaces are not allowed for Sensor ID.',
      };
    }
    if (!companyId && !tid) {
      continue;
    }
    if (!companyId || !tid) {
      return {
        ok: false,
        message:
          'Each sensor row must include both Sensor provider and Sensor ID, or clear the row entirely.',
      };
    }
    const tidNormalized = tid.replace(/\s+/g, '').toLowerCase();
    if (seenTrackingIds.has(tidNormalized)) {
      return {
        ok: false,
        message: `Duplicate sensor tracking ID "${tid}" is not allowed.`,
      };
    }
    seenTrackingIds.add(tidNormalized);

    const match = sensorsList.find(
      (s) =>
        String(s.id) === String(companyId) ||
        String(s.sensor_id) === String(companyId)
    );
    if (!match?.sensor_id) {
      return { ok: false, message: 'Invalid sensor provider selection.' };
    }
    sensors.push({
      sensor_id: String(match.sensor_id),
      sensor_tracking_id: tid,
      priority: sensors.length + 1,
      ...(options.includeProductIds
        ? { product_ids: r.product_ids ?? [] }
        : {}),
    });
  }
  return { ok: true, sensors };
}
